import { inject, Injectable, NgZone } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { HashHandler, MemoryStorage, OidcDiscoveryDoc, TokenResponse } from 'angular-oauth2-oidc';
import queryString from 'query-string';
import { BehaviorSubject, interval, of, Subscription, timer } from 'rxjs';
import { catchError, map, takeWhile, tap } from 'rxjs/operators';
import { clientConfig } from 'src/client-config';
import { logger } from '../pbir/utils/logger';
import { GenericAlertButton, GenericAlertData, GenericAlertDialog } from '../shared/dialogs';

const STATE_KEY = 'insights:auth:state';
const WAIT_MAX_DURATION = 1000 * 5;
const WAIT_CHECK_INTERVAL = 400;

const LOGIN_REDIRECT_PATHNAME = '/signin-oidc';
const LOGIN_REDIRECT_URI = window.location.origin + LOGIN_REDIRECT_PATHNAME;
const POST_LOGOUT_REDIRECT_URI = window.location.origin + '/';

const REFRESH_CALLBACK = 'insights:auth:refresh_callback';
const REFRESH_UPDATE_INTERVAL = 1000 * 5;

const RESPONSE_TYPE = 'code';

const SESSION_CHECK_INTERVAL = 500;

// Time to stop deidling after user interaction.
const DEIDLE_SLEEP_DURATION = 5000;

const IDLE_AFTER_TIME = 30 * 1000;

enum AuthServiceTask {
  LoggingIn = 'logging_in',
  LoggingOut = 'logging_out',
  Refresh = 'refresh'
}

enum AuthServiceDialog {
  Expired = 'expired',
  ExpiresSoon = 'expires_soon',
  Logout = 'logout'
}

interface AuthServiceState {
  nonce?: string;

  task?: AuthServiceTask;
  taskNonce?: string;
  taskTime?: number;

  verifier?: string;

  tokenResponse?: TokenResponse;
  userState?: AuthUserState;
  userInfo?: Record<string, any>;

  tokensLoadedAt?: number;
  sessionAcquiredAt?: number;
  sessionExpiresAt?: number;

  lastActiveAt?: number;
  
  refreshing?: boolean;

  dialog?: AuthServiceDialog;

  logoutData?: {
    idToken?: string;
    userLogout?: boolean;
  },
}

interface ChannelMessage {
  type: string;
  content: string;
}

interface AuthUserState {
  redirectTo: string;
}

const LOGGER = logger('AUTH');

@Injectable({ providedIn: 'root' })
export class AuthService {
  readonly ngZone = inject(NgZone);

  readonly sessionConfig = {
    showSessionExpiryDialogPeriodMs: 5 * 1000 * 60,
    accessTokenRefreshPeriodMs: 7 * 1000 * 60,
    ...clientConfig.sessionConfig
  };


  get refreshing() {
    return this._refreshing$.value || this.state.refreshing;
  }

  set refreshing(value: boolean) {
    this._refreshing$.next(value);

    this.state = {
      ...this.state,
      refreshing: value
    };
  }

  get loggedIn() {
    return !this._loggingOut && !!this.accessToken;
  }


  get updating() {
    return this._loggingOut || !!this.state.task;
  }
  
  get ready() {
    return !this.updating && this.loggedIn;
  }


  get shouldRedirect() {
    return window.location.pathname === LOGIN_REDIRECT_PATHNAME;
  }

  get redirectTo() {
    return this.state.userState?.redirectTo;
  }

  get idToken() {
    return this.state.tokenResponse?.id_token;
  }

  get accessToken() {
    return this.state.tokenResponse?.access_token;
  }

  get refreshToken() {
    return this.state.tokenResponse?.refresh_token;
  }

  get claims(): Record<string, any> {
    return this.state.userInfo ?? {};
  }


  get tokensLoadedAt() {
    return this.state.tokensLoadedAt ? new Date(this.state.tokensLoadedAt) : null;
  }

  get sessionAcquiredAt() {
    return this.state.sessionAcquiredAt ? new Date(this.state.sessionAcquiredAt) : null;
  }

  get sessionExpiresAt() {
    return this.state.sessionExpiresAt ? new Date(this.state.sessionExpiresAt) : null;
  }

  set sessionExpiresAt(value: Date) {
    this.state = {
      ...this.state,
      sessionExpiresAt: value.getTime()
    };
  }


  get lastActiveAt() {
    return this.state.lastActiveAt ? new Date(this.state.lastActiveAt) : null;
  }

  set lastActiveAt(value: Date) {
    this.state = {
      ...this.state,
      lastActiveAt: value.getTime()
    };
  }

  get isIdle() {
    return !this.lastActiveAt || (Date.now() - this.lastActiveAt.getTime()) >= IDLE_AFTER_TIME;
  }


  get dialog() {
    return this.state.dialog;
  }

  set dialog(value: AuthServiceDialog) {
    this.state = {
      ...this.state,
      dialog: value
    };
  }


  get userLogout() {
    return this.state.logoutData?.userLogout;
  }

  set userLogout(value: boolean) {
    this.state = {
      ...this.state,
      logoutData: {
        ...this.state.logoutData,
        userLogout: value
      }
    };
  }


  pauseDeidle = false;

  constructor(
    private _store: Store,
    private _router: Router,
    private _hashHandler: HashHandler,
    private _dialog: MatDialog
  ) { }

  private _lastLog: string;

  //#region Init
  private _userInfo: any;

  async init(): Promise<boolean> {
    LOGGER.debug(`init`);

    await this.loadDiscoveryDocument();
    await this.loadJwks();

    if (window.location.pathname == LOGIN_REDIRECT_PATHNAME) {
      LOGGER.debug(`init: loginCallback`);

      if (!await this.loginCallback()) {
        LOGGER.debug(`init: loginCallback: failed`);
        return false;
      }
    }

    if (!this.loggedIn) {
      LOGGER.debug(`init: notLoggedIn`);
      return false;
    }

    await this.applyState('init');

    try {
      LOGGER.debug(`userinfo_endpoint`);

      const result = await fetch(this._discoveryDocument.userinfo_endpoint, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${this.accessToken}` },
        cache: 'no-store'
      });

      if (result.status >= 400)
        throw new Error(`Status: ${result.status}`);

      this.state = {
        ...this.state,
        userInfo: await result.json()
      };

      if (this.loggedIn)
        this._loggedIn = true;

      return this.loggedIn;
    }
    catch (err) {
      LOGGER.error(`Failed to load user info`, err);
      return false;
    }
    finally {
      this.setupSessionChecks();
    }
  }
  //#endregion

  //#region State
  private _storage = window.localStorage ? window.localStorage : new MemoryStorage();

  private get state(): AuthServiceState {
    const value = this._storage.getItem(STATE_KEY);
    if (!value)
      return {};

    try {
      const state = <AuthServiceState>JSON.parse(value);
      if (!state || typeof state !== 'object')
        throw new Error('Invalid state value.');

      return state;
    }
    catch (err) {
      LOGGER.error(`Invalid auth state in storage.`, err, value);
      return {};
    }
  }

  private set state(value: AuthServiceState) {
    this._storage.setItem(STATE_KEY, JSON.stringify(value));
  }
  //#endregion

  //#region Login
  private _loggedIn = false;

  async login() {
    LOGGER.debug(`login`);

    let taskId;

    try {
      taskId = await this.startTask(AuthServiceTask.LoggingIn, { logoutThrow: true });

      const login = await this.createLogin();

      this.state = {
        ...this.state,
        nonce: login.nonce,
        verifier: login.verifier
      };

      setTimeout(() => this.navigateTo(login.url));
    }
    finally {
      this.finishTask(taskId);
    }
  }

  private async loginCallback() {
    LOGGER.debug(`loginCallback`);

    let success = false;

    let taskId;

    try {
      taskId = await this.startTask(AuthServiceTask.LoggingIn, { logoutThrow: true });

      const query = queryString.parse(window.location.search);

      const error = <string>query['error'];
      if (error) {
        LOGGER.error(`OAuth error`, { error });
        throw new Error(error);
      }

      const code = <string>query['code'];
      const userState = <string>query['state'];

      const verifier = this.state.verifier;
      if (!verifier) {
        LOGGER.error(`Missing challenge verifier`, { state: this.state });
        throw new Error('Missing challenge verifier');
      }

      try {
        const query = [
          `grant_type=${encodeURIComponent('authorization_code')}`,
          `code=${encodeURIComponent(code)}`,
          `redirect_uri=${encodeURIComponent(LOGIN_REDIRECT_URI)}`,
          `code_verifier=${encodeURIComponent(verifier)}`,
          `client_id=${encodeURIComponent(this._clientId)}`,
        ].join('&');

        const result = await fetch(this._discoveryDocument.token_endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: query
        });

        if (result.status >= 400) {
          throw new Error(`Status: ${result.status}`);
        }

        if (userState) {
          try {
            this.state = {
              ...this.state,
              userState: JSON.parse(atob(userState))
            };
          }
          catch (err) {
            LOGGER.warn(`Failed to parse user state.`, { userState });

            this.state = {
              ...this.state,
              userState: {
                redirectTo: null
              }
            };
          }
        }

        this.setTokens(<TokenResponse>await result.json());
        success = true;
        return true;
      }
      catch (err) {
        LOGGER.error(`Failed to handle login callback.`, err);
        return false;
      }
    }
    finally {
      this.finishTask(taskId);

      if (success)
        await this.applyState('loginCallback');
    }
  }

  private async createLogin(options?: { redirectTo?: string }) {
    const userState = btoa(JSON.stringify(<AuthUserState>{
      redirectTo: options?.redirectTo
        ?? (window.location.pathname == LOGIN_REDIRECT_PATHNAME ? null : window.location.pathname)
    }));

    const redirectUri = LOGIN_REDIRECT_URI;
    const [verifier, challenge] = await this.createChallenge();
    const nonce = this.createRandomString();

    const query = [
      `response_type=${encodeURIComponent('code')}`,
      `client_id=${encodeURIComponent(this._clientId)}`,
      `state=${encodeURIComponent(userState)}`,
      `redirect_uri=${encodeURIComponent(redirectUri)}`,
      `scope=${encodeURIComponent(this._scope)}`,
      `code_challenge=${encodeURIComponent(challenge)}`,
      `code_challenge_method=S256`,
      `nonce=${encodeURIComponent(nonce)}`,
    ].join('&');

    return {
      url: `${this._discoveryDocument.authorization_endpoint}?${query}`,
      nonce,
      verifier
    };
  }

  private async createChallenge() {
    const verifier = this.createRandomString();

    return [verifier, this.base64UrlEncode(await this._hashHandler.calcHash(verifier, 'sha-256'))];
  }

  private createRandomString() {
    /*
      * This alphabet is from:
      * https://tools.ietf.org/html/rfc7636#section-4.1
      *
      * [A-Z] / [a-z] / [0-9] / "-" / "." / "_" / "~"
      */
    const unreserved =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let size = 45;
    let id = '';

    const crypto =
      typeof self === 'undefined' ? null : self.crypto || self['msCrypto'];
    if (crypto) {
      let bytes = new Uint8Array(size);
      crypto.getRandomValues(bytes);

      // Needed for IE
      if (!bytes.map) {
        (bytes as any).map = Array.prototype.map;
      }

      bytes = bytes.map((x) => unreserved.charCodeAt(x % unreserved.length));
      id = String.fromCharCode.apply(null, bytes);
    } else {
      while (0 < size--) {
        id += unreserved[(Math.random() * unreserved.length) | 0];
      }
    }

    return this.base64UrlEncode(id);
  }

  private setTokens(tokenResponse: TokenResponse) {
    const rawAccessToken = tokenResponse.access_token;
    if (!rawAccessToken)
      return null;

    const part = atob(rawAccessToken.split('.')[1]);
    const accessTokenParams = JSON.parse(part);

    this.state = {
      ...this.state,
      tokenResponse,
      tokensLoadedAt: Date.now(),
      sessionAcquiredAt: accessTokenParams['auth_time'] * 1000,
      sessionExpiresAt: accessTokenParams['exp'] * 1000,
      logoutData: {
        ...this.state.logoutData,
        idToken: tokenResponse.id_token
      }
    };
  }
  //#endregion

  //#region logout
  private _loggingOut = false;
  private _logout: Promise<void>;

  async logout(userLogout: boolean) {
    if (this._loggingOut)
      return;
      
    this.userLogout = userLogout;
      
    this._loggingOut = true;

    LOGGER.debug(`logout`);

    let redirectUri = '/';

    const logoutData = this.state.logoutData;

    let taskId;

    try {
      taskId = await this.startTask(AuthServiceTask.LoggingOut, {});

      if (this._discoveryDocument.revocation_endpoint) {
        const tokensToRevoke = [
          [this.refreshToken, 'refresh_token'],
          [this.accessToken, 'access_token']
        ].filter(t => !!t);

        for (const [token, hint] of tokensToRevoke) {
          try {
            if (!token)
              continue;

            const query = [
              `token=${encodeURIComponent(token)}`,
              `token_type_hint=${encodeURIComponent(hint)}`,
              `client_id=${encodeURIComponent(this._clientId)}`,
            ].join('&');

            await fetch(this._discoveryDocument.revocation_endpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
              body: query
            });
          }
          catch (err) {
            LOGGER.warn(`Error occurred when calling revocation endpoint for '${hint}'.`, err);
          }
        }
      }

      try {
        this.state = {
          // keep logout data for any tab that refreshes while logging out
          dialog: this.state.dialog,
          logoutData,
          task: AuthServiceTask.LoggingOut,
          taskTime: Date.now()
        };

        if (this._discoveryDocument.end_session_endpoint) {
          const idToken = logoutData?.idToken ?? this.idToken;

          const query = [
            `post_logout_redirect_uri=${encodeURIComponent(POST_LOGOUT_REDIRECT_URI)}`,
            `client_id=${encodeURIComponent(this._clientId)}`,
          ];

          if (idToken)
            query.push(`id_token_hint=${encodeURIComponent(idToken)}`);

          const queryString = query.join('&');

          redirectUri = `${this._discoveryDocument.end_session_endpoint}?${queryString}`;
        }
      }
      catch (err) {
        LOGGER.warn(`Error occurred when calling end session endpoint.`, err);
      }
    }
    finally {
      try {
        this.finishTask(taskId);
      }
      finally {
        try {
          (window as any).InsightsIdpLogoutCallback();
        }
        finally {
          setTimeout(() => this.navigateTo(redirectUri));

        }
      }
    }
  }
  //#endregion

  //#region Operations
  private _wait: Promise<any>;

  private async waitForCurrentTask(options: { logoutThrow?: boolean, timeoutThrow?: boolean }): Promise<WaitForTaskResult> {
    if (!this.state.task) {
      await timer(WAIT_CHECK_INTERVAL).toPromise();
          
      if (!this.state.task)
        return WaitForTaskResult.Success;
    }
          
    let waitStartTime = Date.now();
    let result = WaitForTaskResult.Success;
        
    while (true) {
      const state = this.state;

      let startTime = state.taskTime ?? waitStartTime;
      let timedOut = state.task && (Date.now() - startTime) >= WAIT_MAX_DURATION;

      if (!state.task || this._loggingOut || timedOut) {
        if (this._loggingOut) {
          result = WaitForTaskResult.LoggedOut;
        }

        if (timedOut) {
          result = WaitForTaskResult.TimedOut;
        }

        break;
      }
      else {
        await timer(WAIT_CHECK_INTERVAL).toPromise();
      }
    }
      
    if (result == WaitForTaskResult.LoggedOut) {
      LOGGER.warn(`waitForCurrentTask failed: Logged out`);
    }

    if (result == WaitForTaskResult.TimedOut) {
      LOGGER.warn(`waitForCurrentTask failed: Timed out`);
    }

    if (result != WaitForTaskResult.Success) {
      this.state = {
        ...this.state,
        task: null,
        taskNonce: null,
        taskTime: null
      };

      if (result == WaitForTaskResult.LoggedOut && options.logoutThrow)
        throw new Error('Logged Out');

      if (result == WaitForTaskResult.TimedOut && options.timeoutThrow)
        throw new Error('Timed Out');
    }
  }

  private _taskRunning = false;
  private _startTask: Subscription;

  private async startTask(task: AuthServiceTask, options: { logoutThrow?: boolean, timeoutThrow?: boolean }) {
    try {
      while (this._taskRunning) {
        await timer(WAIT_CHECK_INTERVAL).toPromise();
      }

      this._taskRunning = true;

      LOGGER.debug(`startTask '${task}'`);
      
      const nonce = `${task}.${this.createRandomString()}`;

      let taskState = {
        task,
        taskNonce: nonce,
        taskTime: new Date().getTime(),
      };

      await this.waitForCurrentTask(options);

      this.state = {
        ...this.state,
        ...taskState
      };

      this._startTask = interval(WAIT_CHECK_INTERVAL).pipe(
        map(() => {
          if (!this._taskRunning)
            return false;

          if (this.state.taskNonce != nonce) {
            LOGGER.error(`Task nonce mismatch`, { task });
            return false;
          }

          this.state = {
            ...this.state,
            taskTime: new Date().getTime(),
          }

          return true;
        }),
        takeWhile(b => b),
      ).subscribe();

      return nonce;
    }
    catch (err) {
      this._taskRunning = false;
      LOGGER.error(`Error while updating task state`, { task, err });
      throw err;
    }
  }

  private finishTask(taskId: any) {
    LOGGER.debug(`finishTask '${taskId}'`);

    this._taskRunning = false;

    if (this._startTask) {
      this._startTask.unsubscribe();
      this._startTask = null;
    }

    if (this.state.taskNonce == taskId) {
      this.state = {
        ...this.state,
        task: null,
        taskNonce: null,
        taskTime: null,
      };
    }
  }
  //#endregion

  //#region Refresh
  private _refreshing$ = new BehaviorSubject(false);

  async tryRefresh(applyState = true) {
    LOGGER.debug(`tryRefresh`);

    let success = false;

    let taskId;

    try {
      taskId = await this.startTask(AuthServiceTask.Refresh, { logoutThrow: true });

      this.refreshing = true;

      if (!this.refreshToken)
        throw new Error('Missing refresh token');

      const query = [
        `refresh_token=${encodeURIComponent(this.refreshToken)}`,
        `grant_type=${encodeURIComponent('refresh_token')}`,
        `client_id=${encodeURIComponent(this._clientId)}`,
      ].join('&');

      const result = await fetch(this._discoveryDocument.token_endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: query
      });

      if (result.status >= 400) {
        throw new Error(`Status: ${result.status}`);
      }

      this.setTokens(<TokenResponse>await result.json());
      success = true;
      
      if (this.loggedIn)
        this._loggedIn = true;

      return this.loggedIn;
    }
    catch (err) {
      LOGGER.error(`Failed to refresh.`, { err });
      return false;
    }
    finally {
      try {
        this.finishTask(taskId);

        if (success) {
          if (applyState) {
            timer(0).subscribe(() => this.applyState('refresh'));
          }
        }
        else {
          await this.login();
        }
      }
      finally {
        this.refreshing = false;
      }
    }
  }
  //#endregion

  //#region OIDC
  private get _issuer() { return clientConfig.authority; }
  private get _clientId() { return clientConfig.clientId ?? 'Insights'; }
  private get _scope() { return clientConfig.scope; }

  private _discoveryDocument: OidcDiscoveryDoc;
  private _jwks: any;

  private async loadDiscoveryDocument(): Promise<void> {
    LOGGER.debug(`loadDiscoveryDocument`);

    try {
      const url = [this._issuer, '.well-known/openid-configuration'].join('/');
      const result = await fetch(url, { method: 'GET', cache: 'no-store' });
      if (result.status >= 400)
        throw new Error(`Status: ${result.status}`);

      this._discoveryDocument = <OidcDiscoveryDoc>await result.json();
    }
    catch (err) {
      LOGGER.error(`Failed to load discovery document.`, err);
      throw err;
    }
  }

  private async loadJwks() {
    LOGGER.debug(`loadJwks`);

    try {
      const url = this._discoveryDocument.jwks_uri;
      const result = await fetch(url, { method: 'GET', cache: 'no-store' });
      if (result.status >= 400)
        throw new Error(`Status: ${result.status}`);

      this._jwks = await result.json();
    }
    catch (err) {
      LOGGER.error(`Failed to load jwks.`, err);
      throw err;
    }
  }
  //#endregion

  //#region Session Checks
  private _deidleChecks = false;
  private _deidleHandler = () => this.deidle();
  private _dialogRef: MatDialogRef<any>;
  private _visibleDialog: AuthServiceDialog;
  private _disableDeidle = false;

  private setupSessionChecks() {
    interval(SESSION_CHECK_INTERVAL).pipe(
      tap(() => {
        if (!this._applyState)
          this.applyState('sessionCheck');
      }),
      catchError(err => of(null))
    ).subscribe();
  }

  private toggleDeidling(enabled: boolean) {
    if (enabled == this._deidleChecks)
      return;

    this._deidleChecks = enabled;

    this.ngZone.runOutsideAngular(() => {
      const deidleEvents = ['mousedown', 'mousemove', 'click', 'keypress', 'scroll', 'touchstart', 'focus'];

      if (enabled) {
        document.addEventListener('visibilitychange', this._deidleHandler);

        for (const e of deidleEvents)
          window.addEventListener(e, this._deidleHandler);
      }
      else {
        document.removeEventListener('visibilitychange', this._deidleHandler);

        for (const e of deidleEvents)
          window.removeEventListener(e, this._deidleHandler);
      }
    });
  }


  private _deidleTimer;

  private deidle() {
    if (this.pauseDeidle || this._disableDeidle)
      return;

    this.lastActiveAt = new Date();

    this._disableDeidle = true;
    this.toggleDeidling(false);

    this.ngZone.runOutsideAngular(() => {
      if (this._deidleTimer)
        clearTimeout(this._deidleTimer);

      this._deidleTimer = setTimeout(() => {
        this._disableDeidle = false;
        this.toggleDeidling(true);
      }, DEIDLE_SLEEP_DURATION);
    });
  }


  private _applyState: Promise<boolean>;

  private async applyState(context: string) {
    try {
      if (!this._applyState) {
        this._applyState = this.runApplyState();
      }
      else {
        while (this._applyState) {
          await timer(50).toPromise();
        }
      }

      return await this._applyState;
    }
    catch (err) {
      LOGGER.error(`applyState error`, err);
      return false;
    }
    finally {
      this._applyState = null;
    }
  }

  private async runApplyState(skipLogout = false) {
    const waitResult = await this.waitForCurrentTask({ logoutThrow: false, timeoutThrow: false });
    const loggedOut = waitResult == WaitForTaskResult.LoggedOut || (this._loggedIn && !this.loggedIn);

    if (loggedOut) {
      this._loggingOut = true;
    }
    
    let newDialog = this.dialog;

    if (this._loggingOut) {
      newDialog = this.userLogout ? AuthServiceDialog.Logout : AuthServiceDialog.Expired;

      if (!this.accessToken) {
        setTimeout(() => this.navigateTo('/'));
      }
    }
    else {
      if (!this.ready) {
        return false;
      }

      this.toggleDeidling(true);

      const time = Date.now();

      const loggingIn = this.loggedIn;
      const sessionExpiresAt = this.sessionExpiresAt;
      const tokensLoadedAt = this.tokensLoadedAt;

      if (this.loggedIn) {
        // Time before token expiration when user may not refresh anymore. 3s
        const EXPIRE_SESSION_CUTOFF = 1000 * 2;

        // Time before token expiration when dialog shows prompting user. 5min by default, configurable.
        const DIALOG_SHOW_CUTOFF = this.sessionConfig.showSessionExpiryDialogPeriodMs;

        // Time to start refreshing before dialog appears.
        // This is just iun case token load time is close to it.
        const REFRESH_TIME_BEFORE_DIALOG = 1000 * 60 * 15;

        // Time from token load to start refershing. 1min, just to give some space before firing refreshes.
        const TIME_TO_REFRESH_FROM_TOKEN = 5 * 60 * 1000;

        const expiredAtTime = sessionExpiresAt.getTime() - EXPIRE_SESSION_CUTOFF;
        const dialogAtTime = sessionExpiresAt.getTime() - DIALOG_SHOW_CUTOFF;
        const refreshAtTime = Math.min(
          dialogAtTime - REFRESH_TIME_BEFORE_DIALOG,
          tokensLoadedAt.getTime() + TIME_TO_REFRESH_FROM_TOKEN);

        if (time < expiredAtTime && time < dialogAtTime && time < refreshAtTime) {
          if (this.dialog) {
            newDialog = null;
          }
        }
        else {
          if (time >= expiredAtTime || !this.loggedIn) {
            newDialog = AuthServiceDialog.Expired;
            this.logout(false);
            return false;
          }
          else {
            if (this.dialog === AuthServiceDialog.Expired)
              newDialog = null;

            if (time >= dialogAtTime) {
              newDialog = AuthServiceDialog.ExpiresSoon;
            }
            else {
              if (time >= refreshAtTime) {
                if (!this.isIdle && !this.refreshing) {
                  if (!await this.tryRefresh(false))
                    return false;
                }
              }
            }
          }
        }
      }
    }

    this.dialog = newDialog;

    this.updateDialogState();

    return !this._loggingOut;
  }

  private updateDialogState() {
    if (this._dialogRef) {
      if (this.dialog != this._visibleDialog) {
        this._dialogRef.close();
        this._dialogRef = null;
      }
    }

    if (this.dialog) {
      if (this._dialogRef)
        return true;

      this._visibleDialog = this.dialog;

      if (this.dialog === AuthServiceDialog.Expired) {
        this.showExpiredSessionDialog();
      }
      else if (this.dialog === AuthServiceDialog.ExpiresSoon) {
        this.showUserPromptDialog();
      }
      else if (this.dialog === AuthServiceDialog.Logout) {
        this.showLogoutDialog();
      }

      this._dialogRef.afterClosed().subscribe(() => this._dialogRef = null);
    }
    else {
      this._visibleDialog = null;

      if (this._dialogRef) {
        this._dialogRef.close();
        this._dialogRef = null;
      }
    }
  }

  private showExpiredSessionDialog() {
    this._dialogRef = this._dialog.open(GenericAlertDialog, {
      width: '450px',
      backdropClass: 'backdrop-blur-10',
      hasBackdrop: true,
      disableClose: true,
      panelClass: 'dialog-no-padding',

      data: <GenericAlertData>{
        title: 'Session Expiry',
        content: ['<p>Your session has expired.</p><p>You are being redirected to login.</p>'].join(''),
        buttons: [],
        showClose: false,
      }
    });
  }

  private showUserPromptDialog() {
    this._dialogRef = this._dialog.open(GenericAlertDialog, {
      width: '450px',
      backdropClass: 'backdrop-blur-10',
      hasBackdrop: true,
      disableClose: true,
      data: <GenericAlertData>{
        title: 'Session Expiry',
        content: ['<p>Your session is about to expire and you will be logged out shortly.</p>'].join(''),
        buttons: [GenericAlertButton.cancel.dontClose().onClick(async dialog => {
          LOGGER.debug(`User triggered refresh`);
          await this.tryRefresh(true);
        })],
        showClose: false,
        busy: this._refreshing$
      }
    });
  }

  private showLogoutDialog() {
    this._dialogRef = this._dialog.open(GenericAlertDialog, {
      width: '450px',
      backdropClass: 'backdrop-blur-10',
      hasBackdrop: true,
      disableClose: true,
      panelClass: 'dialog-no-padding',

      data: <GenericAlertData>{
        title: 'Goodbye!',
        content: ['<p>Please wait while you are being logged out</p>'].join(''),
        buttons: [],
        showClose: false,
      }
    });
  }
  //#endregion

  private _navigatingAway;

  private navigateTo(url: string) {
    if (this._navigatingAway)
      return;

    this._navigatingAway = true;

    window.location.href = url;
  }


  private base64UrlEncode(value: string) {
    value = btoa(value);
    return value.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }
}

enum WaitForTaskResult {
  Success,
  LoggedOut,
  TimedOut
}

export function parseJwt(token: string) {
  var base64Url = token.split('.')[1];
  var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
  }).join(''));

  return JSON.parse(jsonPayload);
}

export function clearAuthStateFromStorage() {
  let oauthStorage = window.localStorage;
  if (oauthStorage) {
    oauthStorage.setItem('insights:auth:state', 'null');
    oauthStorage.removeItem('insights:auth:state');
  }
}
